import { useCallback, useMemo, useState } from 'react';
import { useUpdateEffect } from 'common/hooks';
import { AkinatorCompletion, Suggestion } from 'types/suggestions';

type UseSearchbarKeyboardNavigationParams = {
  akinatorCompletions: AkinatorCompletion[] | null;
  showAkinatorCompletions: boolean;
  suggestionItems: Suggestion[] | null;
};

const wrapAkinatorIndex = (index: number, step: number, maxIndex: number) =>
  (index + step + maxIndex + 1) % (maxIndex + 1);

type VerticalNavigationBounds = {
  akinatorCount: number;
  suggestionsCount: number;
  firstSuggestionIndex: number;
  lastSuggestionIndex: number;
};

const getVerticalNavigationBounds = (
  akinatorCount: number,
  suggestionsCount: number,
): VerticalNavigationBounds => ({
  akinatorCount,
  suggestionsCount,
  firstSuggestionIndex: akinatorCount,
  lastSuggestionIndex: akinatorCount + suggestionsCount - 1,
});

const isInAkinatorRange = (index: number, akinatorCount: number) => index >= 0 && index < akinatorCount;

const getAkinatorVerticalIndex = (
  currentIndex: number,
  direction: 1 | -1,
  { suggestionsCount, firstSuggestionIndex, lastSuggestionIndex }: VerticalNavigationBounds,
) => {
  if (!suggestionsCount) return currentIndex;

  return direction === 1 ? firstSuggestionIndex : lastSuggestionIndex;
};

const getDownIndex = (currentIndex: number, bounds: VerticalNavigationBounds) => {
  if (currentIndex < 0) {
    return bounds.akinatorCount > 0 ? 0 : bounds.firstSuggestionIndex;
  }

  if (currentIndex >= bounds.lastSuggestionIndex) {
    return -1;
  }

  return currentIndex + 1;
};

const getUpIndex = (currentIndex: number, bounds: VerticalNavigationBounds) => {
  if (currentIndex < 0) {
    return bounds.suggestionsCount > 0 ? bounds.lastSuggestionIndex : currentIndex;
  }

  if (currentIndex === bounds.firstSuggestionIndex) {
    return bounds.akinatorCount > 0 ? 0 : -1;
  }

  if (currentIndex > bounds.firstSuggestionIndex) {
    return currentIndex - 1;
  }

  return currentIndex;
};

const getVerticalIndex = (
  currentIndex: number,
  direction: 1 | -1,
  akinatorCount: number,
  suggestionsCount: number,
) => {
  const bounds = getVerticalNavigationBounds(akinatorCount, suggestionsCount);

  if (isInAkinatorRange(currentIndex, akinatorCount)) {
    return getAkinatorVerticalIndex(currentIndex, direction, bounds);
  }

  return direction === 1 ? getDownIndex(currentIndex, bounds) : getUpIndex(currentIndex, bounds);
};

export const useSearchbarKeyboardNavigation = ({
  akinatorCompletions,
  showAkinatorCompletions,
  suggestionItems,
}: UseSearchbarKeyboardNavigationParams) => {
  const akinatorCount = showAkinatorCompletions ? (akinatorCompletions?.length ?? 0) : 0;
  const suggestionsCount = suggestionItems?.length ?? 0;

  const combinedItems = useMemo(() => {
    const akinator = showAkinatorCompletions ? (akinatorCompletions ?? []) : [];

    if (!suggestionItems) {
      return akinator.length ? akinator : null;
    }

    return [...akinator, ...suggestionItems];
  }, [showAkinatorCompletions, akinatorCompletions, suggestionItems]);

  const [index, setIndex] = useState(-1);
  const inAkinator = index >= 0 && index < akinatorCount;
  const selectedNavigableItem = combinedItems?.[index] ?? null;

  const reset = useCallback(() => setIndex(-1), []);
  const onMouseEnter = useCallback((nextIndex: number) => setIndex(nextIndex), []);
  const onMouseLeave = useCallback(() => setIndex(-1), []);

  useUpdateEffect(() => {
    const maxIndex = (combinedItems?.length ?? 0) - 1;
    setIndex((current) => Math.min(current, maxIndex));
  }, [combinedItems]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      const { key } = e;

      if (key === 'ArrowLeft' || key === 'ArrowRight') {
        if (!inAkinator) return;

        e.preventDefault();
        e.stopPropagation();
        setIndex((current) => wrapAkinatorIndex(current, key === 'ArrowRight' ? 1 : -1, akinatorCount - 1));

        return;
      }

      let direction: 1 | -1 | null = null;
      if (key === 'ArrowDown') direction = 1;
      if (key === 'ArrowUp') direction = -1;
      if (!direction) return;

      e.preventDefault();
      e.stopPropagation();
      setIndex((current) => getVerticalIndex(current, direction, akinatorCount, suggestionsCount));
    },
    [akinatorCount, inAkinator, suggestionsCount],
  );

  return {
    index,
    reset,
    selectedNavigableItem,
    onKeyDown,
    onMouseEnter,
    onMouseLeave,
  } as const;
};
